import { cx } from "@jugl-web/utils";
import React, { ReactNode, forwardRef } from "react";
import { Button, ButtonVariant, ButtonSize } from "../Button";

export type DeprecatedButtonVariant = "contained" | "outlined" | "text";
export type DeprecatedButtonSize = "regular" | "small";
export type DeprecatedButtonColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "grey"
  | "white"
  | "dark";

type NativeButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export interface DeprecatedButtonProps
  extends Omit<NativeButtonProps, "disabled"> {
  children?: ReactNode;
  color?: DeprecatedButtonColor;
  variant?: DeprecatedButtonVariant;
  size?: DeprecatedButtonSize;
  uppercase?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
}

const mapVariant = (
  variant: DeprecatedButtonVariant,
  color: DeprecatedButtonColor
): ButtonVariant => {
  if (variant === "outlined") return "secondary";
  if (variant === "text") {
    if (color === "tertiary") return "dangerNoBg";
    return "tertiaryNoBg";
  }

  // contained
  switch (color) {
    case "primary":
    case "dark":
      return "primary";
    case "secondary":
      return "secondary";
    case "tertiary":
      return "danger";
    case "grey":
    case "white":
    default:
      return "tertiary";
  }
};

const mapSize = (size: DeprecatedButtonSize): ButtonSize => {
  if (size === "small") return "sm";
  return "md";
};

export const DeprecatedButton = forwardRef<
  HTMLButtonElement,
  DeprecatedButtonProps
>(
  (
    {
      children,
      color = "primary",
      variant = "contained",
      size = "regular",
      uppercase,
      isDisabled,
      fullWidth,
      iconStart,
      iconEnd,
      className,
      ...props
    },
    ref
  ) => {
    const isFullWidth = fullWidth || className?.includes("w-full");

    return (
      <Button
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as unknown as any}
        variant={mapVariant(variant, color)}
        size={mapSize(size)}
        isDisabled={isDisabled}
        fullWidth={fullWidth}
        iconStart={iconStart}
        iconEnd={iconEnd}
        className={cx(
          uppercase && "uppercase",
          className,
          // if the button is full width, we need to enable shrinking to fit the container
          isFullWidth && "shrink"
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

export default DeprecatedButton;
